import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useCurrentUserId } from '#src/app/shared/hooks/session';
import type { ShareActionsOptions } from '#src/app/shared/hooks/share';

export function useGetShareTrackingData(): ShareActionsOptions['getTrackingData'] {
	const userId = useCurrentUserId();
	const sourceLocation = useQueryParamValue('source_location');
	const {
		data: {
			petition: { id, user, photo, bestShareVisual },
		},
	} = usePsfPromoteOrSharePageData();

	const commonTrackingData = useMemo(
		() => ({
			context: 'psf_promote_or_share',
			view_context: 'psf_promote_or_share',
			source_location: sourceLocation,
			petition_id: `petition_${id}`,
			own_petition: userId === user.id,
			showImage: !!photo,
			recruiter_by_id: userId || undefined,
			is_share_asset: !!bestShareVisual?.url,
		}),
		[sourceLocation, id, userId, user.id, photo, bestShareVisual?.url],
	);

	return useCallback(() => commonTrackingData, [commonTrackingData]);
}

export type FacebookPostSheetCopyTrigger = 'facebook_tap' | 'post_cta' | 'group_card' | 'group_cta';

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The Facebook post sheet's funnel events (CHANGE-99696), each carrying the variation that rendered
 * the sheet. Amplitude-only: see `TRACKING_EVENTS.md` for the proto opt-out.
 */
export function useFacebookPostSheetTracking() {
	const track = useTracking();
	const {
		data: {
			psfPromoteOrShareFacebookPostSheetExperiment: { variation },
		},
	} = usePsfPromoteOrSharePageData();

	return useMemo(() => {
		const sheetTrackingData = { facebook_post_sheet_variation: variation };
		return {
			trackViewed: () => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_viewed', sheetTrackingData);
			},
			trackCopied: (trigger: FacebookPostSheetCopyTrigger, copied: boolean) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_copied', {
					...sheetTrackingData,
					facebook_post_sheet_copy_trigger: trigger,
					facebook_post_sheet_copy_result: copied ? 'success' : 'failure',
				});
			},
			trackDismissed: () => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_dismissed', sheetTrackingData);
			},
			trackGroupCardViewed: (groupUrl: string) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_group_card_viewed', {
					...sheetTrackingData,
					recommendation_group_url: groupUrl,
				});
			},
			trackGroupCardTapped: (groupUrl: string) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_group_card_tapped', {
					...sheetTrackingData,
					recommendation_group_url: groupUrl,
				});
			},
		};
	}, [track, variation]);
}
